import { useState, useCallback, useEffect, useMemo } from "react";
import firebaseService from "../../../firebase_service/firebaseService";
import DSChip from "../../system-design/DSChip";
import MsTable from "../../../components/Widgets/Table";
import CurrTime from "../../../components/Widgets/CurrTime";
import { Device, colType } from "../../../Types";
import { onlineCheck, DeviceCommand } from "../../../utils/util";
import DeviceModal from "./DeviceModal";
import { NavLink, useSearchParams } from "react-router-dom";
import ScheduleBulkTaskModal from "./ScheduleBulkTaskModal";
import ButtonLoader from "../../../components/Widgets/ButtonLoader";
import { debounce } from "lodash";
import useClocksInfiniteQuery from "../hooks/useClocksInfiniteQuery";
import adminApiClient, {
  CLOCK_SORT_FIELDS,
  ClockSortDirection,
  ClockSortField,
} from "../../../api/admin";
import DevicesCommands from "./DevicesCommands";
import DSBox from "../../system-design/DSBox";
import DSTextField from "../../system-design/DSTextField";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSTypography from "../../system-design/DSTypography";
import DSAutocomplete from "../../system-design/DSAutocomplete";
import useClockLocationsQuery from "../hooks/useClockLocationsQuery";
import TagSelectionModal from "./TagSelectionModal";
import DSDialog from "../../system-design/DSDialog";
import { KLYDO_YELLOW, KLYDO_GREY } from "../../../consts/colors";
import { usePinnedDevices } from "../../../utils/pinnedDevices";
import PageHeader from "components/PageHeader";
import FilterBar from "components/FilterBar";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import DeviceIdsFilter from "./DeviceIdsFilter";
import MonitorFilter from "./MonitorFilter";
import FeatureBetaGroupFilter from "./FeatureBetaGroupFilter";
import SettingsFilter from "./SettingsFilter";
import {
  isCompleteSettingFilter,
  parseSettingFilters,
  serializeSettingFilters,
} from "../syncedSettings";

// Direct lookup of a single clock by serial, IDF or clock id. Opens the
// device modal on a hit, so it works for unregistered clocks too.
function SerialLookup() {
  const [serial, setSerial] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [device, setDevice] = useState<Device>();

  const lookup = async () => {
    try {
      setDevice(await firebaseService.getDeviceBySerialOrIdf(serial.trim()));
    } catch (e) {
      setNotFound(true);
      setTimeout(() => setNotFound(false), 2000);
    }
  };

  return (
    <>
      <DSBox
        sx={{ display: "flex", alignItems: "center", gap: 1, flex: "0 1 auto" }}
      >
        <DSTextField
          error={notFound}
          helperText={notFound ? "Device not found" : undefined}
          FormHelperTextProps={{ sx: { position: "absolute", top: "100%" } }}
          placeholder="Serial, IDF or Clock ID..."
          size="small"
          value={serial}
          sx={{ width: 240 }}
          onChange={(e) => setSerial(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && serial.trim()) lookup();
          }}
          type="text"
        />
        <ButtonLoader
          title="search"
          disabled={!serial.trim()}
          style={{ fontSize: "12px" }}
          onClick={lookup}
        />
      </DSBox>
      {device && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={true}
          onClose={() => setDevice(undefined)}
        >
          <DeviceModal
            setShowModal={(show: boolean) => {
              if (!show) setDevice(undefined);
            }}
            row={device}
          />
        </DSDialog>
      )}
    </>
  );
}

function SearchUnRegDevice({
  setAdvancedSearchResults,
  registrationStatus,
  devFilter,
}: {
  setAdvancedSearchResults: (data: Device[]) => void;
  registrationStatus: "ALL" | "NOT_REGISTERED" | "REGISTERED";
  devFilter: "ALL" | "ONLY" | "EXCLUDE";
}) {
  const [devNotFoundErr, setDevNotFoundErr] = useState(false);
  const [email, setEmail] = useState<string>();
  const [orderName, setOrderName] = useState<string>();
  const [unRegDevice, setUnRegDevice] = useState<Device>();
  const [showUnRegModal, setShowUnRegModal] = useState<boolean>(false);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showTagModal, setShowTagModal] = useState(false);

  const handleTagSearch = async () => {
    if (selectedTags.length === 0) return;

    try {
      const res = await adminApiClient.getClocksByTags(
        selectedTags,
        registrationStatus !== "REGISTERED",
        devFilter === "ALL" ? undefined : devFilter === "ONLY",
      );
      setAdvancedSearchResults(res);
    } catch (e) {
      setDevNotFoundErr(true);
      setTimeout(() => {
        setDevNotFoundErr(false);
      }, 2000);
    }
  };

  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
    if (tags.length === 0) {
      setAdvancedSearchResults([]);
    }
  };

  return (
    <>
      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {devNotFoundErr && (
          <DSTypography color="error">Device Not Found</DSTypography>
        )}
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <DSTextField
            error={devNotFoundErr}
            placeholder="By email"
            required
            size="small"
            fullWidth
            sx={{ mr: "5px" }}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            type="text"
          />

          <ButtonLoader
            title="search"
            disabled={!email}
            style={{ fontSize: "12px", width: "50%" }}
            onClick={async () => {
              try {
                const res = await firebaseService.getDeviceByEmail(email!);
                setUnRegDevice(res);
                setShowUnRegModal(true);
              } catch (e) {
                setDevNotFoundErr(true);
                setTimeout(() => {
                  setDevNotFoundErr(false);
                }, 2000);
              }
            }}
          ></ButtonLoader>
        </DSBox>
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <DSTextField
            error={devNotFoundErr}
            placeholder="By order name"
            required
            size="small"
            fullWidth
            sx={{ mr: "5px" }}
            onChange={(e) => {
              setOrderName(e.target.value);
            }}
            type="text"
          />

          <ButtonLoader
            title="search"
            disabled={!orderName}
            style={{ fontSize: "12px", width: "50%" }}
            onClick={async () => {
              try {
                const res = await adminApiClient.getOrderClock(orderName!);
                setAdvancedSearchResults(res);
              } catch (e) {
                setDevNotFoundErr(true);
                setTimeout(() => {
                  setDevNotFoundErr(false);
                }, 2000);
              }
            }}
          ></ButtonLoader>
        </DSBox>

        <DSBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <DSTextField
            placeholder="Search by tags..."
            value={selectedTags.join(", ")}
            size="small"
            fullWidth
            sx={{ mr: "5px" }}
            onClick={() => setShowTagModal(true)}
            InputProps={{ readOnly: true }}
          />

          <ButtonLoader
            title="search"
            disabled={selectedTags.length === 0}
            style={{ fontSize: "12px", width: "50%" }}
            onClick={handleTagSearch}
          />
        </DSBox>
      </DSBox>

      <TagSelectionModal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        selectedTags={selectedTags}
        onTagsChange={handleTagsChange}
      />
      {showUnRegModal && unRegDevice && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={true}
          onClose={() => setShowUnRegModal(false)}
        >
          <DeviceModal setShowModal={setShowUnRegModal} row={unRegDevice} />
        </DSDialog>
      )}
    </>
  );
}

// Device display-mode filter (the `mode` setting on the device).
const MODE_FILTER_OPTIONS = ["FEED", "FAVORITE", "COLLECTIONS", "ALL"];

// Cycle filter — whether the device's active cycle mode is fixed or cycling.
// The server resolves the active cycle mode from the device's current mode
// (feed/favorites/collection cycle mode); a null value defaults to daily.
const CYCLE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "FIXED", label: "Fixed" },
  { value: "NOT_FIXED", label: "Not fixed (cycling)" },
];

// The fleet-wide target versions from the Firestore `general/config` doc,
// shown next to the version range inputs so filtering by "everything below
// public" does not mean a trip to the Versions page. Already cached by the
// firebase service, so this costs no fetch.
const VersionReference = () => {
  const general = firebaseService.general;
  if (!general) return null;
  const entries = [
    { label: "obsolete", value: general.obsolete },
    { label: "public", value: general.public },
    { label: "beta", value: general.beta },
    { label: "last", value: general.last },
  ].filter((entry) => entry.value != null);
  if (!entries.length) return null;
  return (
    <DSBox display={"flex"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
      <DSTypography color="text.secondary">Versions:</DSTypography>
      {entries.map(({ label, value }) => (
        <DSChip
          key={label}
          size="small"
          variant="outlined"
          label={`${label} ${value}`}
        />
      ))}
    </DSBox>
  );
};

// MsTable persists the active sort column under this key and the direction
// under `${id}Reverse`.
const DEVICES_TABLE_ID = "devices";

function Devices() {
  const [tableId] = useState(DEVICES_TABLE_ID);

  // Sorting is done by the API (`cancelSort` below), but MsTable still owns the
  // header arrows and persists them under the table id. Seed from the same keys
  // or the arrow and the query disagree after a reload. MsTable's "reversed"
  // means ascending — an unreversed column shows an arrow-down.
  const [sort, setSort] = useState<{
    sortBy: ClockSortField;
    sortDir: ClockSortDirection;
  }>(() => {
    const storedField = localStorage.getItem(DEVICES_TABLE_ID);
    return {
      sortBy: CLOCK_SORT_FIELDS.includes(storedField as ClockSortField)
        ? (storedField as ClockSortField)
        : "heartbeat",
      sortDir:
        localStorage.getItem(`${DEVICES_TABLE_ID}Reverse`) === "true"
          ? "asc"
          : "desc",
    };
  });

  // Mirrors MsTable's own toggle: clicking the active column flips the
  // direction, a new column starts descending.
  const handleSortClick = useCallback((field: string) => {
    if (!CLOCK_SORT_FIELDS.includes(field as ClockSortField)) return;
    setSort((prev) =>
      prev.sortBy === field
        ? { ...prev, sortDir: prev.sortDir === "desc" ? "asc" : "desc" }
        : { sortBy: field as ClockSortField, sortDir: "desc" },
    );
  }, []);
  const [allSelected, setAllSelected] = useState(false);
  const [allRemoved, setAllRemoved] = useState(false);
  const [selected, setSelected] = useState<Array<Device>>([]);
  const [currentSelected, setCurrentSelected] = useState(false);
  const [AggSelection, setAggSelection] = useState<Array<Device>>();
  const [filteredSelected, setFilteredSelected] = useState<Array<Device>>([]);
  const [deviceAction, setDeviceAction] = useState<DeviceCommand | null>(null);
  // The full filtered result set fetched via the "All" checkbox (no
  // pagination). When set, it becomes the table's data so the rows shown and
  // the rows count match the selection. Cleared whenever the filters change.
  const [allMatching, setAllMatching] = useState<Device[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const devFilter = (searchParams.get("dev") || "ALL") as
    | "ALL"
    | "ONLY"
    | "EXCLUDE";
  const betaFilter = (searchParams.get("beta") || "ALL") as
    | "ALL"
    | "ONLY"
    | "EXCLUDE";
  const artistFilter = (searchParams.get("artist") || "ALL") as
    | "ALL"
    | "ONLY"
    | "EXCLUDE";
  const registrationStatus = (searchParams.get("registration") || "ALL") as
    | "ALL"
    | "NOT_REGISTERED"
    | "REGISTERED";
  const textSearch = searchParams.get("query") || "";
  const [advancedSearchResults, setAdvancedSearchResults] = useState<Device[]>(
    [],
  );
  const [pinError, setPinError] = useState<string>("");
  const { data: clockLocations } = useClockLocationsQuery();
  const selectedCountry = searchParams.get("country") || "";
  const selectedCity = searchParams.get("city") || "";
  const selectedTimeZone = searchParams.get("timeZone") || "";
  const selectedRegion = searchParams.get("region") || "";
  const selectedMode = searchParams.get("mode") || "";
  const selectedCycle = searchParams.get("cycle") || "";
  const idsParam = searchParams.get("ids");
  const selectedIds = useMemo(
    () => (idsParam ? idsParam.split(",") : []),
    [idsParam],
  );
  const selectedMonitor = searchParams.get("monitor") || "";
  const selectedFeatureBetaGroup = searchParams.get("featureBetaGroup") || "";
  const settingsParam = searchParams.get("settings");
  const settingFilters = useMemo(
    () => parseSettingFilters(settingsParam),
    [settingsParam],
  );
  // Rows still being filled in (no value picked yet) stay in the URL but are
  // not sent: the API rejects them.
  const settingsQuery = useMemo(() => {
    const complete = settingFilters.filter(isCompleteSettingFilter);
    return complete.length
      ? complete.map(({ field, value }) => `${field}:${value}`)
      : undefined;
  }, [settingFilters]);
  const filteredLocations = useMemo(() => {
    if (!clockLocations) return [];
    let filtered = clockLocations;
    if (selectedCountry)
      filtered = filtered.filter((e) => e.country_code === selectedCountry);
    if (selectedCity)
      filtered = filtered.filter((e) => e.city === selectedCity);
    if (selectedTimeZone)
      filtered = filtered.filter((e) => e.timeZone === selectedTimeZone);
    return filtered;
  }, [clockLocations, selectedCountry, selectedCity, selectedTimeZone]);
  const countryOptions = useMemo(() => {
    if (!clockLocations) return [];
    const relevant =
      selectedCity || selectedTimeZone ? filteredLocations : clockLocations;
    return Array.from(
      new Set(relevant.map((e) => e.country_code).filter(Boolean)),
    ).sort();
  }, [clockLocations, filteredLocations, selectedCity, selectedTimeZone]);
  const cityOptions = useMemo(() => {
    if (!clockLocations) return [];
    const relevant =
      selectedCountry || selectedTimeZone ? filteredLocations : clockLocations;
    return Array.from(
      new Set(relevant.map((e) => e.city).filter(Boolean)),
    ).sort();
  }, [clockLocations, filteredLocations, selectedCountry, selectedTimeZone]);
  const timeZoneOptions = useMemo(() => {
    if (!clockLocations) return [];
    const relevant =
      selectedCountry || selectedCity ? filteredLocations : clockLocations;
    return Array.from(
      new Set(relevant.map((e) => e.timeZone).filter(Boolean)),
    ).sort();
  }, [clockLocations, filteredLocations, selectedCountry, selectedCity]);
  const regionOptions = useMemo(() => {
    if (!clockLocations) return [];
    return Array.from(
      new Set(clockLocations.map((e) => e.region).filter(Boolean)),
    ).sort();
  }, [clockLocations]);
  const {
    data: machinesNewData,
    totalCount,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({
    query: textSearch,
    onlyDevClocks:
      devFilter === "ALL" ? undefined : devFilter === "ONLY" ? true : false,
    onlyBetaClocks:
      betaFilter === "ALL" ? undefined : betaFilter === "ONLY" ? true : false,
    onlyArtistClocks:
      artistFilter === "ALL"
        ? undefined
        : artistFilter === "ONLY"
          ? true
          : false,
    registrationStatus,
    fromVersion: searchParams.get("fromVersion")
      ? parseFloat(searchParams.get("fromVersion")!)
      : undefined,
    toVersion: searchParams.get("toVersion")
      ? parseFloat(searchParams.get("toVersion")!)
      : undefined,
    heartbeatMinutes: searchParams.get("heartbeatMinutes")
      ? parseFloat(searchParams.get("heartbeatMinutes")!)
      : undefined,
    country: searchParams.get("country") || undefined,
    city: searchParams.get("city") || undefined,
    timeZone: searchParams.get("timeZone") || undefined,
    region: searchParams.get("region") || undefined,
    mode: searchParams.get("mode") || undefined,
    cycle: searchParams.get("cycle") || undefined,
    settings: settingsQuery,
    ids: selectedIds.length ? selectedIds : undefined,
    monitorId: selectedMonitor || undefined,
    featureBetaGroupId: selectedFeatureBetaGroup || undefined,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir,
  });

  // A full (non-pagination) fetch is in flight while we already have data to
  // show — e.g. a background refetch on window refocus, or reloading a
  // previously-cached filter combo. Drives a non-blocking "Refreshing…"
  // indicator above the table rather than unmounting it: unmounting would swap
  // the table for a spinner and tear down any open device modal (its open
  // state lives inside the table component). The full-screen loader is instead
  // gated on `isLoading`, which is only true on the initial load when there is
  // no data yet. Not shown while the fetched-all set is displayed or while
  // paginating.
  const fetchingFilter = isFetching && !isFetchingNextPage && !allMatching;

  const { pinnedDevices, pinnedDeviceIds, isPinned, pinDevice, unpinDevice } =
    usePinnedDevices();

  // Fetch every device matching the current filters with no pagination and
  // select them all, so a bulk command reaches the full result set rather than
  // only the rows currently loaded in the paginated table.
  const fetchAllMatching = useCallback(async (): Promise<Device[]> => {
    const all = await adminApiClient.getClocks({
      skip: 0,
      limit: 0,
      query: textSearch,
      devClocks:
        devFilter === "ALL" ? undefined : devFilter === "ONLY" ? true : false,
      betaClocks:
        betaFilter === "ALL" ? undefined : betaFilter === "ONLY" ? true : false,
      artistClocks:
        artistFilter === "ALL"
          ? undefined
          : artistFilter === "ONLY"
            ? true
            : false,
      registrationStatus,
      fromVersion: searchParams.get("fromVersion")
        ? parseFloat(searchParams.get("fromVersion")!)
        : undefined,
      toVersion: searchParams.get("toVersion")
        ? parseFloat(searchParams.get("toVersion")!)
        : undefined,
      heartbeatMinutes: searchParams.get("heartbeatMinutes")
        ? parseFloat(searchParams.get("heartbeatMinutes")!)
        : undefined,
      country: selectedCountry || undefined,
      city: selectedCity || undefined,
      timeZone: selectedTimeZone || undefined,
      region: selectedRegion || undefined,
      mode: selectedMode || undefined,
      cycle: selectedCycle || undefined,
      settings: settingsQuery,
      ids: selectedIds.length ? selectedIds : undefined,
      monitorId: selectedMonitor || undefined,
      featureBetaGroupId: selectedFeatureBetaGroup || undefined,
      fetchAll: true,
    });
    const devices = all as unknown as Device[];
    // Become the table's data source so the visible rows and the rows count
    // match the full selection.
    setAllMatching(devices);
    return devices;
  }, [
    textSearch,
    devFilter,
    betaFilter,
    artistFilter,
    registrationStatus,
    searchParams,
    selectedCountry,
    selectedCity,
    selectedTimeZone,
    selectedRegion,
    selectedMode,
    selectedCycle,
    settingsQuery,
    selectedIds,
    selectedMonitor,
    selectedFeatureBetaGroup,
  ]);

  // Any filter change invalidates the fetched-all set, returning the table to
  // the normal paginated view.
  useEffect(() => {
    setAllMatching(null);
  }, [searchParams]);

  const handleFreeSearch = useCallback(
    debounce((s: string) => {
      const current = new URLSearchParams(window.location.search);
      if (s) {
        current.set("query", s);
      } else {
        current.delete("query");
      }
      setSearchParams(current);
    }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      handleFreeSearch.cancel();
    };
  }, [handleFreeSearch]);

  // The API sends an ISO string, but pinned rows are Device snapshots restored
  // from localStorage (see usePinnedDevices) and can still hold the old
  // BigQuery `{ value }` wrapper indefinitely - until the device is re-pinned.
  const registerProductDate = (
    registerProduct?: string | { value?: string },
  ) => {
    const timestamp =
      typeof registerProduct === "object"
        ? registerProduct?.value
        : registerProduct;
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  useEffect(() => {
    if (textSearch.length) {
      setAdvancedSearchResults([]);
    }
  }, [textSearch.length]);

  const handlePinToggle = (device: Device) => {
    if (isPinned(device.id)) {
      unpinDevice(device.id);
      setPinError("");
    } else {
      const result = pinDevice(device);
      if (result.success) {
        setPinError("");
      } else {
        setPinError(result.error || "Failed to pin device");
        setTimeout(() => setPinError(""), 3000);
      }
    }
  };

  const tableColumns: Array<colType> = [
    {
      size: 40,
      noHeadline: true,
      sort: false,
      label: "Pin",
      field: "pin",
    },
    {
      size: 40,
      noHeadline: true,
      sort: false,
      label: "Online",
      field: "online",
    },
    {
      sort: true,
      label: "Device ID",
      field: "idf",
    },
    {
      sort: false,
      label: "Client",
      field: "clientName",
    },
    {
      sort: true,
      label: "Location",
      field: "location",
    },
    {
      sort: true,
      label: "Heartbeat",
      field: "heartbeat",
    },
    {
      size: 90,
      sort: false,
      label: "Clock Time",
      field: "clockTime",
    },
    {
      sort: true,
      label: "Current Klydo id",
      field: "currentIdf",
      size: 300,
    },
    {
      sort: true,
      label: "registred",
      field: "registerProduct",
    },
    {
      size: 48,
      sort: false,
      label: "Version",
      field: "version",
    },
  ];

  function genActions() {
    return (
      <DevicesCommands
        devicesIds={selected.map((device) => device.id)}
        devices={selected}
      />
    );
  }

  const tableRows = (r: Device, key: number) => {
    const city = r.location?.city ? r.location.city : "no city";
    const country = r.location?.country_code
      ? r.location.country_code
      : "no country";
    const deviceIsPinned = pinnedDeviceIds.has(r.id);

    return {
      pin: (
        <i
          className="fa fa-thumbtack"
          style={{
            color: deviceIsPinned ? KLYDO_YELLOW : KLYDO_GREY,
            cursor: "pointer",
            fontSize: "16px",
          }}
          title={deviceIsPinned ? "Unpin device" : "Pin device"}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handlePinToggle(r);
          }}
        />
      ),
      idf: <p title={r.idf}>{r.idf}</p>,
      clientName: r.clientName || "-no info-",
      location: city + ", " + country,
      clockTime: r.clocktime ? (
        <CurrTime time={r.clocktime} offset={r.timeOffset?.value}></CurrTime>
      ) : (
        "-no info-"
      ),
      currentIdf: r.klydo && (
        <NavLink
          to="/admin/klydos"
          onClick={() => {
            localStorage.setItem("klydossearch", r.klydo!.id);
          }}
          title={r.klydo?.id}
        >
          {r.klydo?.id}
        </NavLink>
      ),
      heartbeat: new Date(r.heartbeat)?.toLocaleString(undefined, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      }),
      registerProduct: registerProductDate(r.registerProduct) || "-no info-",
      version: (
        <NavLink
          to={r.version ? "/admin/versions" : ""}
          onClick={() => {
            if (r.version) {
              localStorage.setItem(
                "versionssearch",
                r.version.toString() || "",
              );
            }
          }}
        >
          {r.version || "-no info-"}
        </NavLink>
      ),
      online: (
        <DSBox
          sx={{
            display: "inline-flex",
            color: onlineCheck(new Date(r.heartbeat))
              ? "success.main"
              : "error.main",
          }}
        >
          <i
            className={`fa ${r.premium && r.premium.length ? "fa-star" : "fa-circle"}`}
          ></i>
        </DSBox>
      ),
      key: key,
    };
  };

  const currentData = useMemo(() => {
    const data =
      advancedSearchResults.length && !textSearch.length
        ? advancedSearchResults
        : machinesNewData!;

    if (!textSearch && data) {
      const unpinned = data.filter(
        (device) =>
          !pinnedDevices.some(
            (pinnedDevice: Device) => pinnedDevice.id === device.id,
          ),
      );
      return [...pinnedDevices, ...unpinned];
    }

    return data;
  }, [advancedSearchResults, textSearch, machinesNewData, pinnedDevices]);

  const resultCount = allMatching
    ? allMatching.length
    : advancedSearchResults.length && !textSearch.length
      ? advancedSearchResults.length
      : totalCount;

  const activeFilterCount = [
    devFilter !== "ALL",
    betaFilter !== "ALL",
    artistFilter !== "ALL",
    registrationStatus !== "ALL",
    Boolean(searchParams.get("fromVersion")),
    Boolean(searchParams.get("toVersion")),
    Boolean(searchParams.get("heartbeatMinutes")),
    Boolean(selectedCountry),
    Boolean(selectedCity),
    Boolean(selectedTimeZone),
    selectedIds.length > 0,
    Boolean(selectedMonitor),
    Boolean(selectedFeatureBetaGroup),
    ...settingFilters.map(isCompleteSettingFilter),
  ].filter(Boolean).length;

  return (
    <>
      <PageHeader
        overline="Devices"
        title="Devices"
        subtitle="Every Klydo clock in the field — status, firmware and configuration"
      />
      <FilterBar
        search={{
          defaultValue: textSearch,
          onChange: handleFreeSearch,
          placeholder: "Device search",
          maxWidth: 280,
        }}
        afterSearch={<SerialLookup />}
        activeCount={activeFilterCount}
        defaultOpen={false}
      >
        <DeviceIdsFilter
          ids={selectedIds}
          knownDevices={machinesNewData}
          onChange={(ids) => {
            if (ids.length) {
              searchParams.set("ids", ids.join(","));
            } else {
              searchParams.delete("ids");
            }
            setSearchParams(searchParams);
          }}
        />
        <MonitorFilter
          monitorId={selectedMonitor}
          onChange={(monitorId) => {
            if (monitorId) {
              searchParams.set("monitor", monitorId);
            } else {
              searchParams.delete("monitor");
            }
            setSearchParams(searchParams);
          }}
        />
        <FeatureBetaGroupFilter
          groupId={selectedFeatureBetaGroup}
          onChange={(groupId) => {
            if (groupId) {
              searchParams.set("featureBetaGroup", groupId);
            } else {
              searchParams.delete("featureBetaGroup");
            }
            setSearchParams(searchParams);
          }}
        />
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Dev:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ width: "140px" }}
            value={devFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (value && value !== "ALL") {
                searchParams.set("dev", value);
              } else {
                searchParams.delete("dev");
              }
              setSearchParams(searchParams);
            }}
          >
            <DSMenuItem value="ALL">All</DSMenuItem>
            <DSMenuItem value="ONLY">Only Dev</DSMenuItem>
            <DSMenuItem value="EXCLUDE">Non-Dev</DSMenuItem>
          </DSTextField>
        </DSBox>
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Beta:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ width: "140px" }}
            value={betaFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (value && value !== "ALL") {
                searchParams.set("beta", value);
              } else {
                searchParams.delete("beta");
              }
              setSearchParams(searchParams);
            }}
          >
            <DSMenuItem value="ALL">All</DSMenuItem>
            <DSMenuItem value="ONLY">Only Beta</DSMenuItem>
            <DSMenuItem value="EXCLUDE">Non-Beta</DSMenuItem>
          </DSTextField>
        </DSBox>
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Artist:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ width: "140px" }}
            value={artistFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (value && value !== "ALL") {
                searchParams.set("artist", value);
              } else {
                searchParams.delete("artist");
              }
              setSearchParams(searchParams);
            }}
          >
            <DSMenuItem value="ALL">All</DSMenuItem>
            <DSMenuItem value="ONLY">Only Artist</DSMenuItem>
            <DSMenuItem value="EXCLUDE">Non-Artist</DSMenuItem>
          </DSTextField>
        </DSBox>
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Registration:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ width: "160px" }}
            value={registrationStatus}
            onChange={(e) => {
              const value = e.target.value;
              if (value && value !== "ALL") {
                searchParams.set("registration", value);
              } else {
                searchParams.delete("registration");
              }
              setSearchParams(searchParams);
            }}
          >
            <DSMenuItem value="ALL">All</DSMenuItem>
            <DSMenuItem value="NOT_REGISTERED">Not Registered</DSMenuItem>
            <DSMenuItem value="REGISTERED">Registered</DSMenuItem>
          </DSTextField>
        </DSBox>
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Mode:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ width: "160px" }}
            value={selectedMode}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                searchParams.set("mode", value);
              } else {
                searchParams.delete("mode");
              }
              setSearchParams(searchParams);
            }}
          >
            <DSMenuItem value="">All</DSMenuItem>
            {MODE_FILTER_OPTIONS.map((m) => (
              <DSMenuItem key={m} value={m}>
                {m}
              </DSMenuItem>
            ))}
          </DSTextField>
        </DSBox>
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Cycle:</DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ width: "180px" }}
            value={selectedCycle}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                searchParams.set("cycle", value);
              } else {
                searchParams.delete("cycle");
              }
              setSearchParams(searchParams);
            }}
          >
            <DSMenuItem value="">All</DSMenuItem>
            {CYCLE_FILTER_OPTIONS.map((c) => (
              <DSMenuItem key={c.value} value={c.value}>
                {c.label}
              </DSMenuItem>
            ))}
          </DSTextField>
        </DSBox>
        <DSAutocomplete
          options={countryOptions}
          value={selectedCountry || null}
          onChange={(_, value) => {
            if (value) {
              searchParams.set("country", value);
            } else {
              searchParams.delete("country");
            }
            setSearchParams(searchParams);
          }}
          renderInput={(params) => (
            <DSTextField {...params} label="Country" size="small" />
          )}
          sx={{ width: 160 }}
          size="small"
        />
        <DSAutocomplete
          options={cityOptions}
          value={selectedCity || null}
          onChange={(_, value) => {
            if (value) {
              searchParams.set("city", value);
            } else {
              searchParams.delete("city");
            }
            setSearchParams(searchParams);
          }}
          renderInput={(params) => (
            <DSTextField {...params} label="City" size="small" />
          )}
          sx={{ width: 180 }}
          size="small"
        />
        <DSAutocomplete
          options={timeZoneOptions}
          value={searchParams.get("timeZone") || null}
          onChange={(_, value) => {
            if (value) {
              searchParams.set("timeZone", value);
            } else {
              searchParams.delete("timeZone");
            }
            setSearchParams(searchParams);
          }}
          renderInput={(params) => (
            <DSTextField {...params} label="Timezone" size="small" />
          )}
          sx={{ width: 220 }}
          size="small"
        />
        <DSAutocomplete
          options={regionOptions}
          value={selectedRegion || null}
          onChange={(_, value) => {
            if (value) {
              searchParams.set("region", value);
            } else {
              searchParams.delete("region");
            }
            setSearchParams(searchParams);
          }}
          renderInput={(params) => (
            <DSTextField {...params} label="Region" size="small" />
          )}
          sx={{ width: 160 }}
          size="small"
        />
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>From Version:</DSTypography>
          <DSTextField
            type="text"
            size="small"
            inputProps={{ inputMode: "decimal" }}
            sx={{ width: "100px" }}
            value={searchParams.get("fromVersion") || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                searchParams.set("fromVersion", value);
              } else {
                searchParams.delete("fromVersion");
              }
              setSearchParams(searchParams);
            }}
          />
        </DSBox>
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>To Version:</DSTypography>
          <DSTextField
            type="text"
            size="small"
            inputProps={{ inputMode: "decimal" }}
            sx={{ width: "100px" }}
            value={searchParams.get("toVersion") || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                searchParams.set("toVersion", value);
              } else {
                searchParams.delete("toVersion");
              }
              setSearchParams(searchParams);
            }}
          />
        </DSBox>
        <VersionReference />
        <DSBox display={"flex"} alignItems={"center"} gap={1}>
          <DSTypography>Heartbeat since</DSTypography>
          <DSTextField
            type="text"
            size="small"
            inputProps={{ inputMode: "decimal" }}
            sx={{ width: "80px" }}
            placeholder="X"
            value={searchParams.get("heartbeatMinutes") || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                searchParams.set("heartbeatMinutes", value);
              } else {
                searchParams.delete("heartbeatMinutes");
              }
              setSearchParams(searchParams);
            }}
          />
          <DSTypography>minutes ago</DSTypography>
        </DSBox>
        <SettingsFilter
          filters={settingFilters}
          onChange={(filters) => {
            if (filters.length) {
              searchParams.set("settings", serializeSettingFilters(filters));
            } else {
              searchParams.delete("settings");
            }
            setSearchParams(searchParams);
          }}
        />
        <DSBox sx={{ flexBasis: "100%", maxWidth: 420, minWidth: 0 }}>
          <SearchUnRegDevice
            setAdvancedSearchResults={setAdvancedSearchResults}
            registrationStatus={registrationStatus}
            devFilter={devFilter}
          />
        </DSBox>
      </FilterBar>
      {pinError && (
        <DSTypography color="error" style={{ marginBottom: "8px" }}>
          {pinError}
        </DSTypography>
      )}
      {machinesNewData && !isLoading ? (
        <>
          {fetchingFilter && (
            <DSBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <DSCircularProgress size={18} />
              <DSTypography color="text.secondary">Refreshing…</DSTypography>
            </DSBox>
          )}
          <MsTable
            id={tableId}
            actions={() => genActions()}
            onAction={(selectedDevices: Array<Device>) => {
              setSelected(selectedDevices);
              if (currentSelected) {
                if (allSelected) {
                  setAllSelected(false);
                  setCurrentSelected(false);
                } else if (allRemoved) {
                  setAllRemoved(false);
                  setCurrentSelected(false);
                } else {
                  return;
                }
                setFilteredSelected(AggSelection!);
              }
            }}
            onSelectedChanged={(
              currSelected: Array<Device>,
              currTableData: Array<Device>,
            ) => {
              if (currSelected.length === 0) {
                setCurrentSelected(false);
                return;
              }
              let isAllSelected: boolean = true;
              let isAllRemoved: boolean = true;

              for (let i = 0; i < currTableData.length; i++) {
                if (currSelected.includes(currTableData[i])) {
                  isAllRemoved = false;
                } else {
                  isAllSelected = false;
                }
                if (!isAllRemoved && !isAllSelected) break;
              }
              setAllSelected(isAllSelected);

              setAllRemoved(isAllRemoved);

              setCurrentSelected(
                isAllRemoved
                  ? currTableData.some((device) =>
                      filteredSelected.includes(device),
                    )
                  : true,
              );
              setAggSelection(currSelected);
            }}
            rowBuild={tableRows}
            initSort={sort.sortBy}
            cancelSort
            onSortClick={handleSortClick}
            data={allMatching ?? currentData}
            cols={tableColumns}
            title={
              resultCount === undefined
                ? "Devices"
                : `Devices · ${resultCount.toLocaleString()} matching`
            }
            modal={DeviceModal}
            onScrollDown={allMatching ? undefined : fetchNextPage}
            onCheckAll={fetchAllMatching}
            allowPartialSelect
            actionsLabel="Commands"
            actionsIcon={<SettingsRemoteIcon />}
            shouldHideSearchBar={true}
            fillViewport
          />
        </>
      ) : (
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 6,
          }}
        >
          <DSCircularProgress />
          <DSTypography color="text.secondary">Loading devices…</DSTypography>
        </DSBox>
      )}
      {!!deviceAction && (
        <ScheduleBulkTaskModal
          open={true}
          handleClose={() => {
            setDeviceAction(null);
          }}
          action={deviceAction!}
          selectedDevices={selected.map((device) => device.id)}
        />
      )}
    </>
  );
}

export default Devices;
